import { FC, ReactElement } from "react";

import Options from "./Options";

import styles from "./styles.module.scss";
import Text from "../Text";
import Loader from "../Loader";
import Checkbox from "../Checkbox";

type TableLayout = "auto" | "fixed";

type TableRow = {
    label: string | ReactElement;
    value: string | ReactElement;
    className?: string;
    onClick?: () => void;
    width?: string | number;
};

type TableProps = React.InputHTMLAttributes<HTMLTableElement> & {
    rows: Array<TableRow>;
    data: Array<{ [key: string]: any }>;
    className?: string;
    tableLayout?: TableLayout;
    loading?: boolean;
    showHeaderWhenLoading?: boolean;
    noDataLabel?: string;
    selectable?: boolean;
    selectedIds?: string[];
    onSelectionChange?: (selectedIds: string[]) => void;
};

const Table: FC<TableProps> = ({
    rows,
    data,
    className,
    tableLayout = "auto",
    loading,
    showHeaderWhenLoading = false,
    noDataLabel,
    selectable = false,
    selectedIds = [],
    onSelectionChange,
    ...props
}) => {
    const handleSelectAll = (checked: boolean) => {
        if (onSelectionChange) {
            if (checked) {
                // Only select items on the current page, keep previously selected items
                const newSelectedIds = new Set(selectedIds);
                data.forEach(row => newSelectedIds.add(row.id));
                onSelectionChange(Array.from(newSelectedIds));
            } else {
                // Deselect items on the current page, keep other selected items
                const currentPageIds = new Set(data.map(row => row.id));
                const newSelectedIds = selectedIds.filter(id => !currentPageIds.has(id));
                onSelectionChange(newSelectedIds);
            }
        }
    };

    const handleSelectRow = (id: string, checked: boolean) => {
        if (onSelectionChange) {
            const newSelection = checked
                ? [...selectedIds, id]
                : selectedIds.filter(selectedId => selectedId !== id);
            onSelectionChange(newSelection);
        }
    };

    // Calculate checkbox states based only on current page items
    const currentPageIds = new Set(data.map(row => row.id));
    const selectedCurrentPage = selectedIds.filter(id => currentPageIds.has(id));
    const isAllSelected = data.length > 0 && selectedCurrentPage.length === data.length;
    const isIndeterminate = selectedCurrentPage.length > 0 && selectedCurrentPage.length < data.length;
    return (
        <div className={`${className} ${styles.table_container}`}>
            {loading && !showHeaderWhenLoading ? (
                <div className={styles.loader_container}>
                    <Loader />
                </div>
            ) : (
                <table
                    className={styles.customTable}
                    style={{ tableLayout }}
                    {...props}
                >
                    <thead>
                        <tr>
                            {selectable && (
                                <th style={{ width: 42 }}>
                                    <Checkbox
                                        checked={isAllSelected}
                                        ref={(input) => {
                                            if (input) {
                                                input.indeterminate = isIndeterminate;
                                            }
                                        }}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                            )}
                            {rows.map(
                                ({
                                    label,
                                    value,
                                    className,
                                    onClick,
                                    width,
                                }) => {
                                    return (
                                        <th
                                            key={value.toString()}
                                            className={
                                                className ? className : ""
                                            }
                                            onClick={onClick}
                                            style={
                                                width ? { width } : undefined
                                            }
                                        >
                                            {label}
                                        </th>
                                    );
                                }
                            )}
                            {data.some((row) => row.options) && (
                                <th style={{ width: 42 }}></th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && showHeaderWhenLoading ? (
                            <tr>
                                <td
                                    colSpan={
                                        rows.length +
                                        (selectable ? 1 : 0) +
                                        (data.some((row) => row.options) ? 1 : 0)
                                    }
                                    className={styles.loader_container}
                                >
                                    <Loader />
                                </td>
                            </tr>
                        ) : (
                            data.map((row, index) => (
                            <tr
                                key={row.id}
                                className={
                                    index % 2 === 0 ? styles.even : styles.odd
                                }
                                style={{
                                    cursor: row.onClick ? "pointer" : "default",
                                }}
                                onClick={row.onClick}
                            >
                                {selectable && (
                                    <td style={{ width: 42 }}>
                                        <Checkbox
                                            checked={selectedIds.includes(row.id)}
                                            onChange={(checked) => {
                                                handleSelectRow(row.id, checked);
                                            }}
                                        />
                                    </td>
                                )}
                                {rows.map(({ value: property, width }, i) => {
                                    return (
                                        <td
                                            key={`${property}${i}`}
                                            style={
                                                width ? { width } : undefined
                                            }
                                        >
                                            {typeof property === "string" &&
                                            row[property] !== null
                                                ? row[property]
                                                : "-"}
                                        </td>
                                    );
                                })}
                                {data.some((row) => row.options) && (
                                    <td
                                        style={{ width: 42 }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {row.options &&
                                        row.options.length > 0 ? (
                                            <Options
                                                dropdownDirection={
                                                    data.length > 2 &&
                                                    index >= data.length - 2
                                                        ? "top-right"
                                                        : "bottom-right"
                                                }
                                                options={row.options}
                                            />
                                        ) : null}
                                    </td>
                                )}
                            </tr>
                            ))
                        )}
                    </tbody>
                </table>
            )}
            {!loading && data.length === 0 && (
                <div className={styles.no_data}>
                    <Text variant="subtitle">
                        {noDataLabel || "Aucune donnée"}
                    </Text>
                </div>
            )}
        </div>
    );
};

export default Table;
